import { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useStore } from '@ui/hooks/useStore';
import encodeCol from '@utils/encodeCol';
import parseCol from '@utils/parseCol';
import encodeSortModel from '@utils/encodeSortModel';
import parseSortModel from '@utils/parseSortModel';

export const useParams = () => {
    const { telemetry, sidebar, map, user, modal, lang, app } = useStore();
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const initState = {
        params: params.toString(),
        update: true,
    };
    const [state, setState] = useState(initState);

    useEffect(() => {
        const update = true;
        setState((prev) => ({ ...prev, update }));
    }, [location.search]);

    const { isSidebarHidden } = sidebar;
    const { isTelemetryVisible } = telemetry;
    const { selectedGroupId, selectedDevice } = map;
    const page = app.page;
    const language = lang.lang;
    const tab = sidebar.tab;
    const position = map.currentPosition;
    const zoom = map.zoom;
    const isMapPage =
        page === 'history' ||
        page === 'support' ||
        page === '' ||
        page === 'reports' ||
        page === 'subusers';
    const dateFilter = map.dateFilter;
    const fromDateFilter = map.fromDateFilter;
    const toDateFilter = map.toDateFilter;
    const columnVisibilityModel = { ...telemetry.columnVisibilityModel };
    const [columnVisibilityModelStr, cvmKeys] = encodeCol(
        columnVisibilityModel,
    );

    const paramCallback = (name: string, val: unknown) => {
        setState((prev) => {
            let value = new URLSearchParams(prev.params);
            if (val) value.set(name, String(val));
            else value.delete(name);
            return { ...prev, params: value.toString() };
        });
    };

    useEffect(() => {
        paramCallback('lang', language);

        const p = params.get('p');
        if (p) {
            const position = p.split(',').map((s) => parseFloat(s));
            const [lat, lon] = position;
            map.setPosition({ lat, lon });
            map.setCurrentPosition([lat, lon]);
        }

        const z = params.get('z');
        if (z) {
            const zoom = parseInt(z, 10);
            if (zoom) {
                map.setZoom(zoom);
                map.setReload();
            }
        }

        if (page === '' || page === 'history') {
            const tab = params.get('tab');
            if (tab) sidebar.setTab(tab);
        }

        const dff = params.get('dff');
        const dft = params.get('dft');
        if (dff && dft) {
            const values = [dff, dft].map((val) => {
                const s = parseInt(val, 10);
                return new Date(s);
            });
            const [fromDateFilter, toDateFilter] = values;
            map.setFromDateFilter(fromDateFilter);
            map.setToDateFilter(toDateFilter);
        }
    }, []);

    useEffect(() => {
        if (state.update) {
            const isTelemetryVisible = params.get('tmv') === 'true';
            if (!!isTelemetryVisible) telemetry.setTelemetryVisible();
            else telemetry.resetTelemetryVisible();

            const selectedGroupId = params.get('sg');
            const selectedDevice = params.get('sd');

            if (selectedGroupId) {
                map.setSelectedGroupId(selectedGroupId);
            }
            if (selectedDevice) {
                map.setSelectedDevice(selectedDevice);
            }

            const isSidebarHidden = params.get('sbh') === 'true';
            if (isSidebarHidden) sidebar.hideSideBar();
            else sidebar.showSideBar();

            const language = params.get('lang');
            language && lang.setLanguage(language);

            const df = params.get('df');
            switch (df) {
                case '':
                    map.setDateFilter('last24Hours');
                    break;
                case '1':
                    map.setDateFilter('last3Days');
                    break;
                case '2':
                    map.setDateFilter('lastWeek');
                    break;
                case '3':
                    map.setDateFilter('customDate');
                    if (page === 'history' || page === 'support') {
                        const dff = params.get('dff');
                        const dft = params.get('dft');
                        if (dff && dft) {
                            const values = [dff, dft].map((val) => {
                                const s = parseInt(val, 10);
                                return new Date(s);
                            });
                            const [fromDateFilter, toDateFilter] = values;
                            map.setFromDateFilter(fromDateFilter);
                            map.setToDateFilter(toDateFilter);
                        }
                    }
                    break;
            }

            const columnVisibilityModelStr = params.get('cvm');
            if (columnVisibilityModelStr) {
                const columnVisibilityModel = parseCol(
                    columnVisibilityModelStr,
                    cvmKeys,
                );
                telemetry.setColumnVisibilityModel(columnVisibilityModel);
            }

            const sm = params.get('sm');
            if (sm) {
                const sortModel = parseSortModel(sm);
                telemetry.setSortModel(sortModel);
            }

            const tm = params.get('tm');
            if (tm === '1') map.setTrackMode();
            else map.resetTrackMode();

            const rmodal = params.get('rmodal');
            if (rmodal === '1') modal.openRegister();
            else modal.closeModal();

            const rcs = params.get('rcs');
            if (rcs === '1') user.setConfirmed();
            else user.resetConfirmed();

            const rce = params.get('rce');
            if (rce === '1') user.setConfirmationErr();
            else user.resetConfirmationErr();
        }
        setState((prev) => ({ ...prev, update: false }));
    }, [state.update]);

    useEffect(() => {
        if (!state.update) {
            if (page === 'history' || page === 'support')
                paramCallback('tmv', isTelemetryVisible);
            else paramCallback('tmv', '');
        }
    }, [isTelemetryVisible, page]);
    useEffect(() => {
        if (!state.update) paramCallback('sbh', isSidebarHidden);
    }, [isSidebarHidden]);
    useEffect(() => {
        if (!state.update) paramCallback('lang', language);
    }, [language]);
    useEffect(() => {
        if (!state.update) {
            if (page === '' || page === 'history') paramCallback('tab', tab);
            else paramCallback('tab', '');
        }
    }, [tab, page, state.update]);
    useEffect(() => {
        if (!state.update) {
            if (page === '') paramCallback('sg', selectedGroupId);
            else paramCallback('sg', '');
        }
    }, [selectedGroupId, page, state.update]);
    useEffect(() => {
        if (!state.update) {
            if (page === 'history' || page === 'support')
                paramCallback('sd', selectedDevice);
            else paramCallback('sd', '');
        }
    }, [selectedDevice, page]);
    useEffect(() => {
        if (!state.update) {
            if (isMapPage) paramCallback('p', position);
            else paramCallback('p', '');
        }
    }, [position, isMapPage]);
    useEffect(() => {
        if (!state.update) {
            if (isMapPage) paramCallback('z', zoom);
            else paramCallback('z', '');
        }
    }, [zoom, isMapPage]);
    useEffect(() => {
        if (!state.update) {
            if (page === 'history' || page === 'support') {
                switch (dateFilter) {
                    case 'last24Hours':
                        paramCallback('df', '');
                        break;
                    case 'last3Days':
                        paramCallback('df', '1');
                        break;
                    case 'lastWeek':
                        paramCallback('df', '2');
                        break;
                    case 'customDate':
                        paramCallback('df', '3');
                        break;
                    default:
                        paramCallback('df', '');
                        break;
                }
            } else paramCallback('df', '');
        }
    }, [dateFilter, page]);
    useEffect(() => {
        if (!state.update) {
            if (
                dateFilter === 'customDate' &&
                (page === 'history' || page === 'support')
            ) {
                paramCallback('dff', fromDateFilter.getTime());
            } else {
                paramCallback('dff', '');
            }
        }
    }, [page, dateFilter, fromDateFilter]);
    useEffect(() => {
        if (!state.update) {
            if (
                dateFilter === 'customDate' &&
                (page === 'history' || page === 'support')
            )
                paramCallback('dft', toDateFilter.getTime());
            else paramCallback('dft', '');
        }
    }, [page, dateFilter, toDateFilter]);
    useEffect(() => {
        if (!state.update) {
            if (page === 'history' || page === 'support')
                paramCallback('cvm', columnVisibilityModelStr);
            else paramCallback('cvm', '');
        }
    }, [page, telemetry.columnVisibilityModel]);
    useEffect(() => {
        if (!state.update) {
            const sm = encodeSortModel(telemetry.sortModel);
            if ((page === 'history' || page === 'support') && sm)
                paramCallback('sm', sm);
            else paramCallback('sm', '');
        }
    }, [page, telemetry.sortModel]);
    useEffect(() => {
        if (!state.update && (page === 'history' || page === 'support')) {
            const tm = map.trackMode;
            if (tm) paramCallback('tm', 1);
            else paramCallback('tm', '');
        }
    }, [page, map.trackMode]);
    useEffect(() => {
        if (!state.update && page === 'auth') {
            if (modal.window === 'register') paramCallback('rmodal', 1);
            else paramCallback('rmodal', '');
        }
    }, [page, modal.window]);
    useEffect(() => {
        if (!state.update && page === 'auth') {
            const rcs = user.confirmed;
            if (rcs) paramCallback('rcs', 1);
            else paramCallback('rcs', '');
        }
    }, [page, user.confirmed]);
    useEffect(() => {
        if (!state.update && page === 'auth') {
            const rce = user.confirmationErr;
            if (rce) paramCallback('rce', 1);
            else paramCallback('rce', '');
        }
    }, [page, user.confirmationErr]);

    useEffect(() => {
        if (state.params.length === 0) return;
        const local = new URLSearchParams(state.params);
        const global = new URLSearchParams(app.search);
        const allKeys = [...local.keys(), ...global.keys()];
        const keys = allKeys.filter((val, i) => allKeys.indexOf(val) === i);
        const isEq = keys.every((val) => {
            return global.get(val) === local.get(val);
        });
        if (!isEq) setParams(state.params, { replace: true });
    }, [state.params]);
};
